import Button, {
  ButtonVariantEnum,
} from "@booking/components/atoms/forms/Button";
import { SizeEnum } from "@booking/enums";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import { type LabelResource } from "@search/api/types";
import { FilterSet } from "@search/store/FilterSet";
import { Filters } from "@search/store/types";
import { useSearchStore } from "@search/store/useSearchStore";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";

import { ColorEnum } from "@/enums";
import { type CommonProps } from "@/types";
import { createId } from "@/utils/helpers";
import { gaClickAttrs } from "@/utils/tracking";

import styles from "./FilterPills.module.css";
import ClearFilter from "./molecules/ClearFilter";

const ref = "filter-pills";

interface FilterPillsProps extends Partial<CommonProps> {
  filters: Filters;
  nameResources: LabelResource;
}

function useFilterPills(props: FilterPillsProps) {
  const { color = ColorEnum.DARKGREEN, id = createId(ref) } = props;
  const className = clsx(styles[ref], color && styles[color], props.className);
  const { getPills } = useSearchStore((state) => state);
  const filterSet = new FilterSet(props.filters);

  return {
    className,
    id,
    pills: getPills(),
    filterSet,
  };
}

function FilterPills(props: FilterPillsProps) {
  const { className, filterSet, id, pills } = useFilterPills(props);
  const { nameResources } = props;
  const navigate = useNavigate();

  function clearAll() {
    const newQuery = filterSet.clearAll();

    navigate(newQuery);
  }

  function removePill(name: string) {
    const newQuery = filterSet.removePill(name);

    navigate(newQuery);
  }

  return pills.length > 0 ? (
    <aside className={className} id={id}>
      {pills.map((pill, index) => {
        const { key, name } = pill;
        const text = nameResources[key] || key;

        return (
          <Button
            key={`pill_${index}`}
            icon={faXmark}
            rounded
            size={SizeEnum.SMALL}
            variant={ButtonVariantEnum.OUTLINE}
            onClick={() => removePill(name)}
            {...gaClickAttrs({
              eventGroup: "Pills",
              click: `clear_${name}_click`,
            })}
          >
            {text}
          </Button>
        );
      })}

      {pills.length > 1 && (
        <ClearFilter updater={clearAll} eventGroup="Pills" name="clear_all" />
      )}
    </aside>
  ) : null;
}

export default FilterPills;
